import { useLocalSearchParams } from 'expo-router'
import { Building2, ChevronRight, MapPin } from 'lucide-react-native'
import { StyleSheet, View } from 'react-native'
import { Text as PaperText, useTheme } from 'react-native-paper'
import { formatAddress, hasExpandedAddress } from '@/lib/address'
import type { Customer, CustomerWithAddress } from '@/types/customers'
import { CardLink } from '@/ui/CardLink'

interface CustomerCardProps {
    customer: Customer | CustomerWithAddress
}

export function CustomerCard({ customer }: CustomerCardProps) {
    const params = useLocalSearchParams<{ orgSlug: string }>()

    return (
        <CardLink href={`/${params.orgSlug}/settings/customers/${customer.identifier}`}>
            <View style={styles.cardContent}>
                <CustomerIcon />
                <CustomerInfo customer={customer} />
                <CustomerMeta customer={customer} />
                <ChevronRight size={20} color="#6b7280" />
            </View>
        </CardLink>
    )
}

function CustomerIcon() {
    return (
        <View style={styles.customerIcon}>
            <Building2 size={24} color="#3b82f6" />
        </View>
    )
}

function CustomerInfo({ customer }: { customer: Customer | CustomerWithAddress }) {
    const theme = useTheme()
    const hasAddress = hasExpandedAddress(customer)

    return (
        <View style={styles.customerInfo}>
            <View style={styles.customerHeader}>
                <PaperText variant="headlineSmall" style={styles.customerName} numberOfLines={2}>
                    {customer.name}
                </PaperText>
            </View>

            {customer.notes ? (
                <PaperText
                    variant="bodyMedium"
                    style={[styles.customerNotes, { color: theme.colors.primary }]}
                    numberOfLines={2}
                >
                    {customer.notes}
                </PaperText>
            ) : null}

            {hasAddress ? (
                <View style={styles.customerDetail}>
                    <MapPin size={14} color="#6b7280" style={[styles.mapIcon]} />
                    <PaperText
                        variant="bodyMedium"
                        style={[styles.customerDetailText, { color: theme.colors.primary }]}
                        numberOfLines={1}
                    >
                        {formatAddress(customer.expand.address)}
                    </PaperText>
                </View>
            ) : null}
        </View>
    )
}

function CustomerMeta({ customer }: { customer: Customer | CustomerWithAddress }) {
    const createdDate = new Date(customer.created).toLocaleDateString()

    return (
        <View style={styles.customerMeta}>
            <PaperText variant="bodyMedium" style={styles.customerMetaSlug}>
                {customer.identifier}
            </PaperText>
            <PaperText variant="bodySmall" style={styles.customerMetaDate}>
                Added {createdDate}
            </PaperText>
        </View>
    )
}

const styles = StyleSheet.create({
    cardContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
    },
    customerIcon: {
        width: 48,
        height: 48,
        backgroundColor: '#dbeafe',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
    },
    customerInfo: {
        flex: 1,
        gap: 4,
    },
    customerHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    customerName: {
        fontWeight: '600',
        flex: 1,
    },
    customerNotes: {
        color: '#374151',
    },
    customerDetail: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
    },
    mapIcon: {
        marginTop: 2,
        flexShrink: 0,
    },
    customerDetailText: {
        flex: 1,
    },
    customerMeta: {
        alignItems: 'flex-end',
        gap: 4,
    },
    customerMetaSlug: {
        color: '#374151',
        fontWeight: '500',
    },
    customerMetaDate: {
        color: '#6b7280',
    },
})
